import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Phase 24 — Segmented single-select status filter.
 *
 * Behaviour:
 *  - Options: All / Draft / Submitted / Approved / Rejected.
 *  - Value: null (All) or one canonical status string.
 *  - Syncs to the URL `?status=` param — copy/paste sharable.
 *  - Persists last selection to `localStorage` keyed by `persistKey`.
 *
 * Props:
 *   - value: current status or null
 *   - onChange: (nextStatus | null) => void
 *   - persistKey: string unique per surface, e.g. "hours-filter:worker-history"
 *   - includeRejected: bool — hide the Rejected pill when the surface never
 *     shows rejected sheets (default true).
 */
const OPTIONS = [
  { key: null, label: "All" },
  { key: "draft", label: "Draft" },
  { key: "submitted", label: "Submitted" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function StatusFilter({
  value, onChange, persistKey, includeRejected = true,
}) {
  const [sp, setSp] = useSearchParams();
  // On mount, hydrate from URL param first, then localStorage, then default.
  useEffect(() => {
    const urlVal = sp.get("status");
    if (urlVal !== null) {
      if (urlVal !== (value || "")) onChange(urlVal || null);
      return;
    }
    try {
      const stored = persistKey ? localStorage.getItem(persistKey) : null;
      if (stored !== null && stored !== (value || "")) {
        onChange(stored || null);
      }
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opts = includeRejected ? OPTIONS : OPTIONS.filter((o) => o.key !== "rejected");

  const pick = (nextVal) => {
    onChange(nextVal);
    // URL sync.
    const params = new URLSearchParams(sp);
    if (nextVal) params.set("status", nextVal);
    else params.delete("status");
    setSp(params, { replace: true });
    // Persistence.
    if (persistKey) {
      try {
        if (nextVal) localStorage.setItem(persistKey, nextVal);
        else localStorage.removeItem(persistKey);
      } catch (_) { /* ignore */ }
    }
  };

  return (
    <div
      className="inline-flex items-center gap-1 rounded-full border border-border bg-background p-1 shadow-sm"
      role="tablist"
      data-testid="hours-status-filter"
    >
      {opts.map((o) => {
        const active = (o.key || null) === (value || null);
        return (
          <button
            key={o.key || "all"}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`hours-status-filter-${o.key || "all"}`}
            onClick={() => pick(o.key)}
            className={
              "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
              (active
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-muted")
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
